"use client";

import { useCallback, useRef, useState } from "react";
import { trpc } from "../trpc";

interface Result {
  deletableIds: string[];
  nonDeletableIds: string[];
  isLoading: boolean;
  hasChecked: boolean;
}

// Permissions API allows max 30 checks per request (asset_ids × permission_levels).
// We send one permission level, so cap at 25 IDs per request for safety margin.
const PERMISSION_BATCH_SIZE = 25;

/**
 * Checks can_delete permissions for a batch of type-prefixed asset IDs
 * (e.g. "zap:123", "table:456", "folder:abc"). Groups by asset type
 * and makes one permission check per type. Folders are assumed deletable
 * since there's no granular delete permission for them today.
 *
 * Call `check(ids)` explicitly when the permission check should run (e.g.
 * when the delete modal opens, or on selection change for the toolbar).
 */
export function useBulkCheckDeletePermissions(): Result & {
  check: (ids: string[]) => Promise<void>;
} {
  const utils = trpc.useUtils();
  const requestIdReference = useRef(0);
  const [result, setResult] = useState<Result>({
    deletableIds: [],
    nonDeletableIds: [],
    isLoading: false,
    hasChecked: false,
  });

  const check = useCallback(
    async (ids: string[]) => {
      const requestId = ++requestIdReference.current;
      if (ids.length === 0) {
        setResult({
          deletableIds: [],
          nonDeletableIds: [],
          isLoading: false,
          hasChecked: true,
        });
        return;
      }

      setResult({
        deletableIds: [],
        nonDeletableIds: [],
        isLoading: true,
        hasChecked: false,
      });

      const grouped: Record<string, { rawId: string; typedId: string }[]> = {};
      for (const typedId of ids) {
        const colonIndex = typedId.indexOf(":");
        const type = typedId.slice(0, colonIndex);
        const rawId = typedId.slice(colonIndex + 1);
        if (!grouped[type]) grouped[type] = [];
        grouped[type].push({ rawId, typedId });
      }

      const deletable: string[] = [];

      // Zaps aren't on FGA yet — they inherit permissions through folders.
      // Next Gen Zaps are the same underlying asset and aren't a supported
      // permissionAssetTypeSchema value, so they skip the same way. Skip the
      // FGA check and assume deletable (same as folders).
      const skipFgaCheck = new Set(["folder", "zap", "nextGenZap"]);

      const typesToCheck = Object.entries(grouped).filter(
        ([type]) => !skipFgaCheck.has(type),
      );

      for (const type of skipFgaCheck) {
        if (grouped[type]) {
          for (const item of grouped[type]) {
            deletable.push(item.typedId);
          }
        }
      }

      const batches = typesToCheck.flatMap(([assetType, items]) => {
        const chunks: { rawId: string; typedId: string }[][] = [];
        for (
          let index = 0;
          index < items.length;
          index += PERMISSION_BATCH_SIZE
        ) {
          chunks.push(items.slice(index, index + PERMISSION_BATCH_SIZE));
        }
        return chunks.map((chunk) => ({ assetType, chunk }));
      });

      // Each batch gets its own try/catch so one failing asset type doesn't
      // block the rest; a failed batch is treated as non-deletable (fail-closed).
      await Promise.all(
        batches.map(async ({ assetType, chunk }) => {
          try {
            const data = await utils.permission.checkPermissions.fetch({
              assetIds: chunk.map((item) => item.rawId),
              assetType: assetType as any,
              permissionLevels: ["can_delete"],
            });
            if (!data?.results) return;
            const permMap = new Map<string, boolean>();
            for (const r of data.results) {
              permMap.set(
                String(r.assetId),
                !!(r.permissionLevels as Record<string, boolean>)?.canDelete,
              );
            }
            for (const item of chunk) {
              if (permMap.get(item.rawId)) {
                deletable.push(item.typedId);
              }
            }
          } catch {
            // Permission check failed for this batch. Don't assume deletable --
            // the user will see these assets as non-deletable in the modal.
          }
        }),
      );

      if (requestId !== requestIdReference.current) return;

      const deletableSet = new Set(deletable);
      const nonDeletableIds = ids.filter((id) => !deletableSet.has(id));

      setResult({
        deletableIds: deletable,
        nonDeletableIds,
        isLoading: false,
        hasChecked: true,
      });
    },
    [utils.permission.checkPermissions],
  );

  return { ...result, check };
}
